import { _ } from "../../../core/polyglot/polyglot.js";
import {
	findSelectedItem,
	firstSelectableItem,
} from "../../../core/elements/select/utils.js";
import { select } from "../../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import BudgetBusinessTrendComparisonElement from "../BudgetBusinessTrendComparisonElement.js";
import FiscalYearBusinessTrendComparisonElement from "../FiscalYearBusinessTrendComparisonElement.js";
import ForecastingTrendComparisonElement from "../ForecastingTrendComparisonElement.js";
import regionWidget from "../../../core/widgets/regionWidget.js";

/**
 * Renders a dropdown to choose a comparison element for the business
 * trend document.
 *
 * @param {function} onChange - Called with the selection as argument
 * when the user changes the selection in the dropdown.
 */
export default class BusinessTrendComparisonPicker extends Widget {
	constructor({ onChange }) {
		super(...arguments);

		this._elements = [];
		this._selectedElement = null;

		this._businessTrendPicker = regionWidget({
			content: (html) => {
				if (!this._elements.length) {
					return;
				}

				html.render(
					select({
						ref: (ref) =>
							ref.setAttribute(
								"name",
								"business-trend-comparison-picker",
							),
						items: this._elements,
						selected: this._selectedElement,
						onChange: (value) => {
							this._selectedElement = value;
							onChange(value);
						},
					}),
				);
			},
		});

		this._currentYear = undefined;
		this._allYears = undefined;
		this._allBudgets = undefined;
	}

	hasElements() {
		return this._elements.length > 0;
	}

	setElements({ currentYear, allYears, allBudgets, allForecasts }) {
		this._currentYear = currentYear;
		this._allYears = allYears;
		this._allBudgets = allBudgets;
		this._allForecasts = allForecasts;

		this._setDropdownElements();

		this.update();
	}

	getElement() {
		return this._selectedElement;
	}

	setCurrentFiscalYear(currentYear) {
		this._currentYear = currentYear;
		this._setDropdownElements();
	}

	renderContentOn(html) {
		html.render(this._businessTrendPicker);
	}

	_setDropdownElements() {
		const fiscalYearElements = this._createFiscalYearElements();
		const currentYearBudgetElements =
			this._createCurrentYearBudgetElements();
		const otherYearBudgetElements = this._createOtherYearBudgetElements();
		const forecastElements = this._createForecastElements();

		this._elements = [];
		if (fiscalYearElements) {
			this._elements.push(fiscalYearElements);
		}
		if (currentYearBudgetElements) {
			this._elements.push(currentYearBudgetElements);
		}
		if (otherYearBudgetElements) {
			this._elements.push(otherYearBudgetElements);
		}
		if (forecastElements) {
			this._elements.push(forecastElements);
		}

		this._selectedElement =
			findSelectedItem({
				items: this._elements,
				selected: (item) => item.selected,
			})?.value || firstSelectableItem(this._elements)?.value;

		this._businessTrendPicker.update();
	}

	_createFiscalYearElements() {
		const fiscalYearElements = this._allYears
			.filter((candidateYear) =>
				this._shouldShowFiscalYear(candidateYear),
			)
			.sort(this._sortFiscalYears.bind(this))
			.map((fiscalYear) => {
				const item = new FiscalYearBusinessTrendComparisonElement({
					fiscalYear,
				});
				return {
					label: item.getName(),
					value: item,
					selected: this._isItemSelected(item),
				};
			});
		if (fiscalYearElements.length) {
			return {
				label: _("Fiscal years"),
				options: fiscalYearElements,
			};
		}

		return undefined;
	}

	_sortFiscalYears(year1, year2) {
		return year2.getStart() - year1.getStart();
	}

	_createCurrentYearBudgetElements() {
		return this._createBudgetElements({
			label: _("This year's budgets"),
			filterBudgetFunction: (budget) =>
				budget.getFiscalYearId() === this._currentYear.getId(),
		});
	}

	_createOtherYearBudgetElements() {
		return this._createBudgetElements({
			label: _("Other year's budgets"),
			filterBudgetFunction: (budget) =>
				budget.getFiscalYearId() !== this._currentYear.getId() &&
				budget.isForActiveFiscalYear(),
		});
	}

	_createBudgetElements({ label, filterBudgetFunction }) {
		const otherYearBudgets = this._allBudgets
			.filter(filterBudgetFunction)
			.map((budget) => {
				const item = new BudgetBusinessTrendComparisonElement({
					budget,
				});
				return {
					label: item.getName(),
					value: item,
					selected: this._isItemSelected(item),
				};
			});
		if (otherYearBudgets.length) {
			return {
				label,
				options: otherYearBudgets,
			};
		}
		return undefined;
	}

	_createForecastElements() {
		const forecastElements = this._allForecasts
			.filter((forecast) =>
				forecast.isInPeriod({
					startDate: this._currentYear.getStart(),
					endDate: this._currentYear.getEnd(),
				}),
			)
			.map((forecast) => {
				const item = new ForecastingTrendComparisonElement({
					forecast,
				});

				return {
					label: item.getName(),
					value: item,
					selected: this._isItemSelected(item),
				};
			});

		if (forecastElements.length) {
			return {
				label: _("Published forecasts"),
				options: forecastElements,
			};
		}
		return undefined;
	}

	_isItemSelected(item) {
		return this._selectedElement
			? this._selectedElement.getId() === item.getId()
			: item.isCurrent?.();
	}

	_shouldShowFiscalYear(fiscalYear) {
		return (
			fiscalYear.isActive() &&
			this._canCompareTo(this._currentYear, fiscalYear)
		);
	}

	_canCompareTo(currentFiscalYear, otherFiscalYear) {
		const sameYears = this._fiscalYearsAreTheSame(
			currentFiscalYear,
			otherFiscalYear,
		);

		const startSameMonth = this._fiscalYearsStartSameMonth(
			currentFiscalYear,
			otherFiscalYear,
		);

		const haveSameLength = this._fiscalYearsHaveSameLength(
			currentFiscalYear,
			otherFiscalYear,
		);

		return !sameYears && startSameMonth && haveSameLength;
	}

	_fiscalYearsAreTheSame(fiscalYear1, fiscalYear2) {
		return fiscalYear2.getId() === fiscalYear1.getId();
	}

	_fiscalYearsStartSameMonth(fiscalYear1, fiscalYear2) {
		const month1 = fiscalYear1.asMonths()[0].getPeriodId();
		const month2 = fiscalYear2.asMonths()[0].getPeriodId();

		return month1 === month2;
	}

	_fiscalYearsHaveSameLength(fiscalYear1, fiscalYear2) {
		return fiscalYear1.asMonths().length === fiscalYear2.asMonths().length;
	}
}
